import React from 'react';
import {
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from 'recharts';

export interface TimeSeriesPoint {
    timestamp: string;
    [key: string]: string | number | null | undefined;
}

export interface TimeSeriesSeries {
    key: string;
    label: string;
    color: string;
    unit?: string;
}

export interface TimeSeriesReferenceLine {
    value: number;
    label: string;
    color?: string;
}

interface TimeSeriesChartProps {
    data: TimeSeriesPoint[];
    series: TimeSeriesSeries[];
    height?: number;
    xFormatter?: (value: string) => string;
    yFormatter?: (value: number) => string;
    // Fixes the y-axis top so the chart reads against a known ceiling
    yDomainMax?: number;
    // Draws a dashed guide line, e.g. a capacity limit or a threshold
    referenceLine?: TimeSeriesReferenceLine;
    // Set to false for whole-number series, such as counts, to avoid fractional ticks
    allowDecimals?: boolean;
}

// Renders the rounded, dark tooltip shown on hover
const CustomTooltip: React.FC<any> = ({ active, payload, label, xFormatter }) => {
    if (!active || !payload || !payload.length) {
        return null;
    }

    return (
        <div className='time-series-tooltip'>
            <div className='tooltip-label'>{xFormatter ? xFormatter(label) : label}</div>
            {/* Render one row per series in the tooltip */}
            {payload.map((entry: any) => (
                <div key={entry.dataKey} className='tooltip-row'>
                    <span className='tooltip-dot' style={{ backgroundColor: entry.color }} />
                    <span className='tooltip-name'>{entry.name}</span>
                    <span className='tooltip-value'>{entry.value}{entry.unit ?? ''}</span>
                </div>
            ))}
        </div>
    );
};

const TimeSeriesChart: React.FC<TimeSeriesChartProps> = ({
    data, series, height = 220, xFormatter, yFormatter, yDomainMax, referenceLine, allowDecimals = true,
}) => {
    return (
        <ResponsiveContainer width='100%' height={height}>
            <AreaChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <defs>
                    {/* Define a soft gradient fill per series */}
                    {series.map(item => (
                        <linearGradient key={item.key} id={`gradient-${item.key}`} x1='0' y1='0' x2='0' y2='1'>
                            <stop offset='5%' stopColor={item.color} stopOpacity={0.28} />
                            <stop offset='95%' stopColor={item.color} stopOpacity={0.02} />
                        </linearGradient>
                    ))}
                </defs>
                <CartesianGrid strokeDasharray='3 3' vertical={false} stroke='var(--border-color-light)' />
                <XAxis
                    dataKey='timestamp'
                    tickFormatter={xFormatter}
                    tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                    axisLine={{ stroke: 'var(--border-color-light)' }}
                    tickLine={false}
                    minTickGap={32}
                />
                <YAxis
                    tickFormatter={yFormatter}
                    tick={{ fontSize: 11, fill: 'var(--text-color-lightgray)', fontFamily: 'var(--main-font-light)' }}
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    domain={[0, yDomainMax ?? 'auto']}
                    allowDecimals={allowDecimals}
                />
                <Tooltip content={<CustomTooltip xFormatter={xFormatter} />} />
                {/* Draw the optional capacity or threshold guide line */}
                {referenceLine && (
                    <ReferenceLine
                        y={referenceLine.value}
                        stroke={referenceLine.color ?? 'var(--text-color-lightgray)'}
                        strokeDasharray='4 4'
                        label={{
                            value: referenceLine.label,
                            position: 'insideTopRight',
                            fontSize: 11,
                            fill: referenceLine.color ?? 'var(--text-color-lightgray)',
                        }}
                    />
                )}
                {/* Render one soft area line per series */}
                {series.map(item => (
                    <Area
                        key={item.key}
                        type='monotone'
                        dataKey={item.key}
                        name={item.label}
                        stroke={item.color}
                        strokeWidth={2}
                        fill={`url(#gradient-${item.key})`}
                        isAnimationActive={true}
                        animationDuration={400}
                        connectNulls
                        dot={false}
                        activeDot={{ r: 3 }}
                    />
                ))}
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default TimeSeriesChart;
